import React, { useState } from 'react';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { ChevronDown, ChevronRight, AlertCircle, Sparkles } from 'lucide-react';
import { formatMacro } from '@shared/utils/formatting';
import { useHubUIStore } from '../../../stores/v4/useHubUIStore';
import { PillarMetric } from '../hooks/useMetabolicMatrix';
import { SourceAuditList } from './SourceAuditList';
import { isHazardous, resolveProgressColor, NUTRIENT_REGISTRY, normalizeNutrientKey } from '@/shared/constants/metabolic-pillars';

interface PillarSystemCardProps {
    title: string;
    color: string;
    metrics: PillarMetric[];
}

/**
 * PillarSystemCard: A high-fidelity, collapsible domain card for micronutrients.
 * V8.4: Unified progress colors (White for baseline, Green/Red for overflow).
 * FIXED: Uses NUTRIENT_REGISTRY for pretty labels.
 */
export const PillarSystemCard: React.FC<PillarSystemCardProps> = ({ title, color, metrics }) => {
    const isCollapsed = useHubUIStore(state => state.collapsedSections[title] || false);
    const toggleSection = useHubUIStore(state => state.toggleSection);

    const [expandedMetricId, setExpandedMetricId] = useState<string | null>(null);

    // Calculate overall system health percentage for summary
    const totalProgress = metrics.reduce((acc, curr) => {
        if (!curr.target || curr.target <= 0) return acc;
        return acc + Math.min(1, curr.value / curr.target);
    }, 0) / metrics.length;
    
    const summaryPct = Math.round(totalProgress * 100);

    const numericStyle = { fontVariantNumeric: 'lining-nums', fontFamily: 'ui-sans-serif, system-ui, sans-serif' };

    const handleToggleAudit = (id: string) => {
        setExpandedMetricId(expandedMetricId === id ? null : id);
    };

    return (
        <Box style={{ 
            backgroundColor: 'rgba(255,255,255,0.02)', 
            borderBottomWidth: 1, 
            borderBottomColor: isCollapsed ? 'rgba(255,255,255,0.05)' : `${color}22`,
            marginBottom: 12,
            overflow: 'hidden'
        }}>
            <Pressable 
                onPress={() => toggleSection(title)}
                style={{ padding: 20, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: isCollapsed ? 'transparent' : 'rgba(255,255,255,0.01)' }}
            >
                <Typography style={{ fontSize: 11, fontWeight: '950', color: color, letterSpacing: 1.5 }}>{title}</Typography>
                
                <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    {isCollapsed && (
                        <Typography style={{ ...numericStyle, fontSize: 16, fontWeight: '950', color: color, marginTop: 1 }}>{summaryPct}%</Typography>
                    )}
                    {isCollapsed ? <ChevronRight size={16} color="#444" /> : <ChevronDown size={16} color={color} />}
                </Box>
            </Pressable>

            {!isCollapsed && (
                <Box style={{ paddingHorizontal: 20, paddingBottom: 20, paddingTop: 5, display: 'flex', flexDirection: 'column', gap: 15 }}>
                    {metrics.map(m => {
                        const hasTarget = m.target > 0;
                        const isExceeded = hasTarget && m.value > m.target;
                        const progress = hasTarget ? Math.min(100, (m.value / m.target) * 100) : 0;
                        const overflow = m.value - m.target;
                        const overflowPct = hasTarget ? Math.round((overflow / m.target) * 100) : 0;
                        const isExpanded = expandedMetricId === m.id;
                        
                        const barColor = resolveProgressColor(m.id, m.value, m.target);
                        const isDangerous = isExceeded && isHazardous(m.id);

                        // RESOLVE PRETTY LABEL (e.g. Vitamin B1)
                        const profile = NUTRIENT_REGISTRY[normalizeNutrientKey(m.id)];
                        const prettyLabel = profile?.label || m.label;
                        const subLabel = profile?.subLabel;

                        return (
                            <Box key={m.id}>
                                <Pressable onPress={() => handleToggleAudit(m.id)} style={{ width: '100%' }}>
                                    <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                                        <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                            <Box>
                                                <Typography style={{ fontSize: 12, fontWeight: '900', color: isExpanded ? '#fff' : '#888', textTransform: 'uppercase' }}>{prettyLabel}</Typography>
                                                {subLabel && (
                                                    <Typography style={{ fontSize: 8, fontWeight: '800', color: '#444', textTransform: 'uppercase', marginTop: 1 }}>{subLabel}</Typography>
                                                )}
                                            </Box>
                                            
                                            {/* OVERFLOW BADGE */}
                                            {isExceeded && (
                                                <Box style={{ 
                                                    backgroundColor: isDangerous ? '#ef444422' : '#2ecc7122', 
                                                    padding: '2px 6px', borderRadius: 4, 
                                                    display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4,
                                                    borderWidth: 1, borderColor: isDangerous ? '#ef444444' : '#2ecc7144'
                                                }}>
                                                    {isDangerous ? <AlertCircle size={8} color="#ef4444" /> : <Sparkles size={8} color="#2ecc71" />}
                                                    <Typography style={{ ...numericStyle, fontSize: 8, fontWeight: '900', color: isDangerous ? '#ef4444' : '#2ecc71' }}>
                                                        +{isDangerous ? formatMacro(overflow) : `${overflowPct}%`}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        
                                        <Typography style={{ ...numericStyle, fontSize: 16, fontWeight: '950', color: isExceeded ? barColor : '#fff', marginTop: 1 }}>
                                            {formatMacro(m.value)}
                                            {hasTarget && (
                                                <Typography style={{ ...numericStyle, fontSize: 11, color: '#444', fontWeight: '800' }}> / {m.target}{m.unit}</Typography>
                                            )}
                                            {!hasTarget && (
                                                <Typography style={{ ...numericStyle, fontSize: 11, color: '#444', fontWeight: '800' }}> {m.unit}</Typography>
                                            )}
                                        </Typography>
                                    </Box>
                                    
                                    <Box style={{ height: 6, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 3, overflow: 'hidden' }}>
                                        <Box style={{ 
                                            width: `${progress}%`, 
                                            height: '100%', 
                                            backgroundColor: barColor, 
                                            borderRadius: 3, 
                                            boxShadow: isExceeded ? `0 0 12px ${barColor}66` : 'none',
                                            opacity: (m.value === 0 && hasTarget) ? 0.3 : 1
                                        }} />
                                    </Box>
                                </Pressable>

                                {/* INLINE SOURCE AUDIT */}
                                <SourceAuditList 
                                    sources={m.sources || []} 
                                    unit={m.unit} 
                                    color={barColor === '#e2e8f0' ? color : barColor} 
                                    isVisible={isExpanded} 
                                />
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};
